<template>
  <ok-modal
    :value="value"
    size="lg"
    dialog-class="tables-modal"
    :title="$t('help.'+(editMode?'edit-question':'add-question'))"
    @change="$emit('input',false)"
  >
    <div>
      <error-alert
        v-if="error"
        :error="error"
      />
      <validation-observer ref="form">
        <b-tabs>
          <b-tab
            v-if="form.locale && form.locale['ar']"
            title="Arabic"
          >
            <ok-text-input
              v-model="form.locale['ar'].question"
              is-required
              :label="`${$t('help.question')} (Arabic)`"
              dir="rtl"
            />
            <ok-text-input
              dir="rtl"
              :label="`${$t('help.answer')} (Arabic)`"
              is-required
            >
              <ok-editor
                :value="form.locale['ar'].answer"
                :height="320"
                type="ckeditor4"
                @input="form.locale['ar'].answer=$event"
              />
            </ok-text-input>
          </b-tab>
          <b-tab
            v-if="form.locale && form.locale['en']"
            title="English"
          >
            <ok-text-input
              v-model="form.locale['en'].question"
              :label="$t('help.question')"
              is-required
            />
            <ok-text-input
              :label="`${$t('help.answer')} (English)`"
              is-required
            >
              <ok-editor
                :value="form.locale['en'].answer"
                :height="320"
                type="ckeditor4"
                @input="form.locale['en'].answer=$event"
              />
            </ok-text-input>
          </b-tab>
        </b-tabs>
        <document-input-dropzone
          ref="dropzone"
          v-model="form.video"
          :max-files="1"
          accepted-files="image/jpg, image/png, image/jpeg, video/*"
          @remove="form.remove_video=$event"
        />
      </validation-observer>
    </div>
    <template #actions>
      <div class="d-flex justify-content-end align-items-center">

        <b-button
          variant="outline-dark"
          class="mr-50"
          @click="reset(), $emit('input',false)"
        >
          {{ $t('forms.cancel') }}
        </b-button>
        <b-button
          variant="dark"
          :disabled="loading"
          @click="submit"
        >
          {{ $t('forms.submit') }}
        </b-button>
      </div>
    </template>
  </ok-modal>
</template>

<script>
import { BButton, BTab, BTabs } from 'bootstrap-vue'
import cloneDeep from 'lodash/cloneDeep'
import isString from 'lodash/isString'
import { ValidationObserver } from 'vee-validate'
import OkModal from '@/@bya3/components/modals/OkModal'
import OkTextInput from '@/@bya3/components/inputs/OkTextInput'
import DocumentInputDropzone from '@/@bya3/components/inputs/file-inputs/DocumentInputDropzone'
import FormsService from '@/services/forms-service'
import HelpAPI from '@/api/admin/help-api'
import ErrorHandler from '@/utils/error-handler'
import ErrorAlert from '@/@bya3/components/alerts/ErrorAlert'

export default {
  name: 'ProcessQuestion',
  components: {
    ErrorAlert,
    ValidationObserver,
    DocumentInputDropzone,
    OkEditor: () => import(/* webpackChunkName: "ok-editor" */ '@/@bya3/components/inputs/ok-editor/OkEditor'),
    OkTextInput,
    OkModal,
    BButton,
    BTab,
    BTabs,
  },
  props: {
    question: {
      type: Object,
      default: () => {},
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      form: {},
    }
  },
  computed: {
    editMode() {
      return this.question?.id ?? false
    },
  },
  watch: {
    question() {
      this.setQuestion()
    },
  },
  mounted() {
    this.setQuestion()
  },
  methods: {
    setQuestion() {
      this.reset()
      if (this.editMode) {
        const imageUrl = this.question?.video ?? ''
        if (typeof imageUrl === 'string' && this.isValidURL(imageUrl)) {
          setTimeout(() => { this.addFileManually(imageUrl) }, 200)
        }
        this.form.video = [this.question.video]
        this.question.locales.forEach(locale => {
          this.form.locale[locale.locale] = { question: locale.question, answer: locale.answer }
        })
      }
    },
    isValidURL(string) {
      const res = string.match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)/g)
      return (res !== null)
    },
    reset() {
      this.form = {
        locale: {
          en: {
            question: '',
            answer: '',
          },
          ar: {
            question: '',
            answer: '',
          },
        },
      }
    },
    async submit() {
      this.error = null
      const valid = await (this.$refs.form.validateWithInfo())
      if (!valid.isValid) {
        this.error = { title: this.$tc('errors.field-invalid', 2), message: ErrorHandler.getValidationErrors(valid.errors) }
      } else {
        this.loading = true
        const form = cloneDeep(this.form)
        form.locales = Object.keys(form.locale).map(localeKey => ({ locale: localeKey, ...form.locale[localeKey] }))
        form.locales = JSON.stringify(form.locales)

        if (this.editMode) {
          form.id = this.question.id
        }

        if (isString(form.video)) {
          delete form.video
        } else if (form?.video?.length) {
          form.video = form?.video[0] ?? ''
        }
        form.section_id = this.$route?.params?.id ?? ''
        const formData = FormsService.convertObjectToFormData(form)
        const api = this.editMode ? HelpAPI.edit(formData, 'question') : HelpAPI.add(formData, 'question')
        await api
          .then(response => {
            this.$notifySuccess(response.message)
            this.reset()
            this.$emit('processed')
          })
          .catch(err => {
            this.error = err
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    async addFileManually(imageUrl) {
      const imageName = imageUrl.substring(imageUrl.lastIndexOf('/') + 1)
      this.$refs.dropzone.manuallyAddFile(imageUrl, imageName, 0)
    },
  },
}
</script>
